import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import type { NextLink, Operation, RequestHandler } from '@apollo/client/link/core';
import type { ServerError } from '@apollo/client/link/utils';
import _ from 'lodash';
import {
  EMITTER_CONSTANTS,
  FRONT_END_ERRORS_EMITTER_CONSTANTS,
  HOST_URL,
  TIME_TRACKING_EMITTER_CONSTANTS,
} from '@/shared/config';
import { SELECTED_PORTAL } from '@/shared/config/env';
import auth from '@/shared/lib/auth/auth';
import getAccessToken from '@/shared/lib/auth/authToken';
import emitter from '@/shared/lib/eventEmitter';
import { toastrError } from '@/shared/lib/toastNotification';

/**
 * Recognises the Apollo failure that means the request never reached the server. Narrows the value to an ApolloError and compares its message against the fixed 'Network request failed' text.
 *
 * @param error - Caught value of unknown shape, narrowed before its message is compared.
 * @returns True when the value is an ApolloError reporting a failed network request.
 */
const isNetworkError = (error: unknown) =>
  error instanceof ApolloError && error.message === 'Network request failed';

// Fire-and-forget tracking mutations whose errors are intentionally swallowed.
const ERROR_BYPASS_OPERATIONS = ['callCenterActiveTimeTracking', 'callCenterOnlineTimeTracking'];

const cache = new InMemoryCache({ addTypename: false });

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  console.log('[apolloClient]-ApolloError', { graphQLErrors, networkError });

  // Error handling is not implemented for the tracking mutations in ERROR_BYPASS_OPERATIONS
  if (graphQLErrors || networkError) {
    if (ERROR_BYPASS_OPERATIONS.includes(operation?.operationName)) {
      if (
        graphQLErrors?.length === 1 &&
        graphQLErrors[0].extensions.code === 'INTERNAL_SERVER_ERROR'
      ) {
        console.log('[apolloClient]-ApolloError: INTERNAL_SERVER_ERROR');
        return forward(operation);
      }

      return null;
    }
  }

  if (graphQLErrors) {
    if (
      graphQLErrors.length === 1 &&
      graphQLErrors[0].extensions.code === 'INTERNAL_SERVER_ERROR'
    ) {
      console.log('[apolloClient]-ApolloError: INTERNAL_SERVER_ERROR');
      return forward(operation);
    }

    _.forEach(graphQLErrors, (err) => {
      switch (err.extensions.code) {
        case 'UNAUTHENTICATED': {
          window.isSessionExpired = true;
          emitter.emit(EMITTER_CONSTANTS.EXPIRED_TOKEN, 'request');
          break;
        }
        default: {
          toastrError(err.message, 'Error');
          break;
        }
      }
    });
  }

  if (networkError && networkError.name !== 'AbortError') {
    if ((networkError as ServerError).statusCode === 503) {
      emitter.emit(EMITTER_CONSTANTS.SITE_UNDER_MAINTENANCE);
      return null;
    }

    console.log(`[apolloClient]-ApolloNetworkError: ${networkError}`);
    if (isNetworkError(networkError)) {
      toastrError('Please check your network connection and try again', 'Network error');
    } else {
      toastrError(networkError.message, networkError.name);
    }
  }

  return null;
});

/**
 * Builds the Apollo client the report app talks to the GraphQL API through. Chains an auth link that awaits the current access token — or uses the supplied override — and stamps the access-token, selected-portal and socket-id headers while emitting each operation to the time-tracking and API-spam listeners, then the shared error link and the HTTP link, and disables caching for queries and watchQueries unless caching was requested.
 *
 * @param isUsingCache - When true Apollo's default cache policies are kept; otherwise queries run no-cache. Defaults to false.
 * @param defaultToken - Access token to send instead of the one resolved from the current session.
 * @returns A promise resolving to the configured Apollo client.
 */
const createClient = async (isUsingCache = false, defaultToken?: string) => {
  const httpLink = new HttpLink({
    uri: HOST_URL,
    credentials: 'same-origin',
    fetchOptions: 'no-cors',
  });

  // ApolloLink's RequestHandler type expects a synchronous Observable return, but this
  // handler is async (needs to await the access token) — the same pattern Apollo apps
  // commonly rely on at runtime; the type only reflects the sync case.
  const authLink = new ApolloLink((async (operation: Operation, forward: NextLink) => {
    const accessToken = await getAccessToken();
    operation.setContext(({ headers }: { headers?: Record<string, unknown> }) => {
      const customHeaders = {
        ...headers,
        'selected-portal': SELECTED_PORTAL,
        'socket-id': auth.getSocketId(),
      };
      const headerAccessToken = defaultToken || accessToken;
      if (headerAccessToken) {
        Object.assign(customHeaders, {
          'access-token': headerAccessToken,
        });
      }
      return { headers: customHeaders };
    });
    emitter.emit(TIME_TRACKING_EMITTER_CONSTANTS.API_TRACKING, operation);
    emitter.emit(FRONT_END_ERRORS_EMITTER_CONSTANTS.API_SPAM_TRACKING, operation);
    return forward(operation);
  }) as unknown as RequestHandler);

  return new ApolloClient({
    link: from([authLink, errorLink, httpLink]),
    // name & version variables use for statistic purpose
    name: 'ai-report-processing-desktop',
    version: '0.86.14',
    cache,
    defaultOptions: isUsingCache
      ? undefined
      : {
          watchQuery: {
            fetchPolicy: 'no-cache',
          },
          query: {
            fetchPolicy: 'no-cache',
          },
        },
  });
};

let canClearApolloCache = true;

/**
 * Empties the Apollo store so the next report queries refetch from the server. Skipped while a previous clear is still settling, it builds a client, clears its store, and re-arms the guard 100 ms later.
 *
 * @returns A promise resolving once the store has been cleared, or immediately when a clear is already in flight.
 */
export const clearApolloCache = async () => {
  if (!canClearApolloCache) {
    return;
  }
  canClearApolloCache = false;
  console.log('[apolloClient]-CLEARCACHE');
  const client = await createClient();
  await client.clearStore();
  setTimeout(() => {
    canClearApolloCache = true;
  }, 100);
};

export default createClient;
